import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { toYMD, fromYMD, todayYMD } from '@/utils/taskDates';

const MONTHS_FULL  = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const fmtDisplay = (ymd) => {
  if (!ymd) return '';
  const d = fromYMD(ymd);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
};

/**
 * A friendly date picker with fast month + year navigation.
 * - Click the title to switch between Days → Months → Years views
 * - Arrows step by month / year / decade depending on the active view
 * Renders the calendar in a portal so it floats above modal scroll containers.
 */
const DatePickerField = ({ value, onChange, invalid, onClose, className = '' }) => {
  const [open, setOpen]   = useState(false);
  const [mode, setMode]   = useState('days');               // 'days' | 'months' | 'years'
  const [cursor, setCursor] = useState(() => fromYMD(value || todayYMD()));
  const [pos, setPos]     = useState(null);
  const anchorRef = useRef(null);
  const popRef    = useRef(null);

  const today = todayYMD();

  // Keep the cursor in sync when the value changes externally.
  useEffect(() => { if (value) setCursor(fromYMD(value)); }, [value]);

  const place = () => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 288;
    const height = 332;
    let left = Math.min(r.left, window.innerWidth - width - 8);
    left = Math.max(8, left);
    const below = window.innerHeight - r.bottom;
    const top = below < height + 8 && r.top > below ? r.top - height - 6 : r.bottom + 6;
    setPos({ top, left, width });
  };

  const openPicker = () => { setMode('days'); setCursor(fromYMD(value || todayYMD())); place(); setOpen(true); };
  const closePicker = () => { setOpen(false); onClose && onClose(); };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      closePicker();
    };
    const reposition = () => place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const year  = cursor.getFullYear();
  const month = cursor.getMonth();

  // Day grid (Monday-first)
  const dayCells = useMemo(() => {
    const first = new Date(year, month, 1);
    const lead = (first.getDay() + 6) % 7;
    const days = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d));
    return cells;
  }, [year, month]);

  const decadeStart = Math.floor(year / 10) * 10;
  const years = useMemo(() => Array.from({ length: 12 }, (_, i) => decadeStart - 1 + i), [decadeStart]);

  const stepBack = () => {
    if (mode === 'days')   setCursor(new Date(year, month - 1, 1));
    if (mode === 'months') setCursor(new Date(year - 1, month, 1));
    if (mode === 'years')  setCursor(new Date(year - 10, month, 1));
  };
  const stepFwd = () => {
    if (mode === 'days')   setCursor(new Date(year, month + 1, 1));
    if (mode === 'months') setCursor(new Date(year + 1, month, 1));
    if (mode === 'years')  setCursor(new Date(year + 10, month, 1));
  };

  const pickDay = (d) => { onChange(toYMD(d)); closePicker(); };
  const titleLabel = mode === 'days' ? `${MONTHS_FULL[month]} ${year}` : mode === 'months' ? `${year}` : `${decadeStart} – ${decadeStart + 9}`;
  const cycleTitle = () => setMode((m) => (m === 'days' ? 'months' : m === 'months' ? 'years' : 'days'));

  const btn = 'w-9 h-9 flex items-center justify-center rounded-lg text-sm transition';

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        onClick={() => (open ? closePicker() : openPicker())}
        className={`mt-1 w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm bg-white transition text-left
          ${invalid ? 'border-red-400 bg-red-50/40' : 'border-gray-200'} ${open ? 'ring-2 ring-blue-300' : ''} ${className}`}
      >
        <span className={value ? 'text-gray-800' : 'text-gray-400'}>{value ? fmtDisplay(value) : 'Select a date'}</span>
        <CalendarTodayIcon style={{ fontSize: 16 }} className="text-gray-400 flex-shrink-0" />
      </button>

      {open && pos && createPortal(
        <div
          ref={popRef}
          className="fixed z-[2000] bg-white border border-gray-200 rounded-2xl shadow-2xl p-3"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={stepBack} className="text-gray-500 hover:text-[#002169] hover:bg-gray-100 rounded-lg p-1 transition"><ChevronLeftIcon /></button>
            <button type="button" onClick={cycleTitle} className="px-3 py-1 text-sm font-bold text-[#002169] rounded-lg hover:bg-gray-100 transition">
              {titleLabel}
            </button>
            <button type="button" onClick={stepFwd} className="text-gray-500 hover:text-[#002169] hover:bg-gray-100 rounded-lg p-1 transition"><ChevronRightIcon /></button>
          </div>

          {/* Days view */}
          {mode === 'days' && (
            <>
              <div className="grid grid-cols-7 mb-1">
                {DOW.map((d) => <div key={d} className="text-[11px] font-semibold text-gray-400 text-center py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {dayCells.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const ymd = toYMD(d);
                  const isSel = ymd === value;
                  const isToday = ymd === today;
                  return (
                    <button key={i} type="button" onClick={() => pickDay(d)}
                      className={`${btn} mx-auto
                        ${isSel ? 'bg-[#002169] text-white font-bold'
                          : isToday ? 'border border-[#002169] text-[#002169] font-semibold'
                          : 'text-gray-700 hover:bg-blue-50'}`}>
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Months view */}
          {mode === 'months' && (
            <div className="grid grid-cols-3 gap-1.5 py-1">
              {MONTHS_SHORT.map((m, i) => {
                const isSel = value && fromYMD(value).getFullYear() === year && fromYMD(value).getMonth() === i;
                return (
                  <button key={m} type="button"
                    onClick={() => { setCursor(new Date(year, i, 1)); setMode('days'); }}
                    className={`py-2.5 rounded-lg text-sm font-medium transition ${isSel ? 'bg-[#002169] text-white' : 'text-gray-700 hover:bg-blue-50'}`}>
                    {m}
                  </button>
                );
              })}
            </div>
          )}

          {/* Years view */}
          {mode === 'years' && (
            <div className="grid grid-cols-3 gap-1.5 py-1">
              {years.map((y, i) => {
                const muted = i === 0 || i === years.length - 1;
                const isSel = value && fromYMD(value).getFullYear() === y;
                return (
                  <button key={y} type="button"
                    onClick={() => { setCursor(new Date(y, month, 1)); setMode('months'); }}
                    className={`py-2.5 rounded-lg text-sm font-medium transition
                      ${isSel ? 'bg-[#002169] text-white' : muted ? 'text-gray-300 hover:bg-gray-50' : 'text-gray-700 hover:bg-blue-50'}`}>
                    {y}
                  </button>
                );
              })}
            </div>
          )}

          {/* Footer quick actions */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            <button type="button" onClick={() => pickDay(fromYMD(today))}
              className="text-xs font-semibold text-[#002169] hover:underline">Today</button>
            <button type="button" onClick={closePicker}
              className="text-xs text-gray-400 hover:text-gray-600">Close</button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DatePickerField;
